import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Alert,
  Stack,
  IconButton,
  FormControlLabel,
  LinearProgress,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2/";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CustomButton from "../../common/Button/CustomButton";
import FileUpload from "../../common/FileUpload";
import AddSliderContext from "../../../context/addSlider/AddSliderContext";

// Firebase Storage
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "../../../firebase";

// Convert any uploaded image file to WebP format using HTML5 Canvas
const convertImageToWebP = (
  file,
  maxWidth = 1920,
  maxHeight = 1080,
  quality = 0.85
) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        let width = img.naturalWidth || img.width;
        let height = img.naturalHeight || img.height;

        // Scale proportionally if image exceeds max bounds
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Failed to convert image to WebP"));
              return;
            }
            // Strip old extension and sanitize name
            const cleanBase = (file.name || "slider")
              .replace(/\.[^/.]+$/, "")
              .replace(/[^\w-]/g, "_");
            const fileName = `${Date.now()}_${cleanBase}.webp`;
            const webpFile = new File([blob], fileName, { type: "image/webp" });

            resolve({ file: webpFile, fileName });
          },
          "image/webp",
          quality
        );
      };
      img.onerror = (err) => reject(err);
      img.src = event.target.result;
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
};

const AddSlider = ({ hideModal, handleSetSlider, action, saving = false }) => {
  const sliderContext = useContext(AddSliderContext);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [imageSize, setImageSize] = useState(null);
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleImageChange = (e) => {
    if (isUploading || saving) return;
    setError(null);
    setIsUploading(true);
    setProgress(0);
    const upImage = e.target.files[0];
    e.target.value = "";
    if (upImage) {
      convertImageToWebP(upImage, 1920, 1080, 0.85)
        .then(({ file, fileName }) => {
          if (!isMounted.current) return;
          handleFileUpload({
            path: "images/sliders",
            file,
            fileName,
          });
        })
        .catch((ex) => {
          console.log("WebP Conversion Error:", ex);
          setError("Could not process this image. Try another image file.");
          setIsUploading(false);
        });
    } else {
      setIsUploading(false);
    }
  };

  const handleFileUpload = ({ path, file, fileName }) => {
    const storageRef = ref(storage, `${path}/${fileName}`);
    const metadata = {
      contentType: "image/webp",
    };
    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const prog = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setProgress(prog);
      },
      (error) => {
        setIsUploading(false);
        console.log("Upload Error:", error);
        setError("Image upload failed. Please try again.");
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref)
          .then((downloadURL) => {
            if (!isMounted.current) return;
            setIsUploading(false);
            sliderContext.setState((prev) => ({
              ...prev,
              imageUrl: downloadURL,
              imageName: fileName,
            }));
          })
          .catch((ex) => {
            setIsUploading(false);
            console.log("Error getting download URL:", ex);
            setError("Could not retrieve the uploaded image. Please try again.");
          });
      }
    );
  };

  return (
    <Grid2 container spacing={2} sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#f8fafc", borderRadius: 2 } }}>
      <Grid2 xs={12}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pb: 2, borderBottom: "1px solid #e2e8f0" }}>
          <Box><Typography id="slider-modal-title" variant="h6" sx={{ fontWeight: 800, color: "#1e293b" }}>{action === "ADD" ? "Add Banner Image" : "Edit Banner Image"}</Typography><Typography id="slider-modal-description" variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>Choose an image and configure its homepage appearance.</Typography></Box>
          <IconButton aria-label="Close banner editor" disabled={saving || isUploading} onClick={hideModal}><CloseIcon /></IconButton>
        </Stack>
        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 2 }}>{error}</Alert>}
      </Grid2>

      {/* Image Preview / Upload */}
      <Grid2 xs={12}>
        <Typography variant="subtitle2" color="text.secondary" mb={1}>
          Banner Image (Recommended: 1920 × 800 px widescreen with centered content)
        </Typography>
        {sliderContext.state.imageUrl ? (
          <Box
            sx={{
              position: "relative",
              width: "fit-content",
              maxWidth: "100%",
              mx: "auto",
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid #e0e0e0",
              boxShadow: 1,
            }}
          >
            <Box
              component="img"
              src={sliderContext.state.imageUrl}
              alt={sliderContext.state.title || "Slider preview"}
              onLoad={(event) => {
                const image = event.currentTarget;
                setImageSize({
                  src: sliderContext.state.imageUrl,
                  width: image.naturalWidth,
                  height: image.naturalHeight,
                });
              }}
              sx={{
                display: "block",
                maxWidth: "100%",
                width: "auto",
                height: "auto",
              }}
            />
            <IconButton
              component="label"
              aria-label="Change slider image"
              title="Change image"
              disabled={isUploading || saving}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                "&:hover": { backgroundColor: "#fff" },
                "&.Mui-disabled": {
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                },
              }}
            >
              <EditIcon />
              <input
                type="file"
                accept="image/*"
                aria-label="Change slider image"
                hidden
                disabled={isUploading || saving}
                onChange={handleImageChange}
              />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ width: "100%", height: 180 }}>
            <FileUpload
              className="full-width"
              handleChange={handleImageChange}
              upProgress={progress}
              label="Click or drag & drop slider banner image here"
            />
          </Box>
        )}
        {imageSize && imageSize.src === sliderContext.state.imageUrl && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Image size: {imageSize.width} × {imageSize.height} px
          </Typography>
        )}
        {isUploading && (
          <Box role="status" sx={{ mt: 1.5 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Uploading image… {progress}%
            </Typography>
          </Box>
        )}
      </Grid2>

      {/* Title */}
      <Grid2 xs={12} sm={6}>
        <TextField
          fullWidth
          disabled={saving}
          id="slider-title"
          label="Title / Caption (Optional)"
          variant="outlined"
          size="small"
          value={sliderContext.state.title || ""}
          onChange={(e) => {
            sliderContext.setState({
              ...sliderContext.state,
              title: e.target.value,
            });
          }}
          helperText="Display name or caption for the slider"
        />
      </Grid2>

      {/* Order */}
      <Grid2 xs={12} sm={6}>
        <TextField
          required
          fullWidth
          disabled={saving}
          type="number"
          id="slider-order"
          label="Display Order"
          variant="outlined"
          size="small"
          value={sliderContext.state.order ?? 1}
          onChange={(e) => {
            sliderContext.setState({
              ...sliderContext.state,
              order: parseInt(e.target.value, 10) || 1,
            });
          }}
          helperText="Slides are ordered ascending (1, 2, 3...)"
          inputProps={{ min: 1 }}
        />
      </Grid2>

      {/* Target Link */}
      <Grid2 xs={12}>
        <TextField
          fullWidth
          disabled={saving}
          id="slider-link"
          label="Target Link / Redirect URL (Optional)"
          variant="outlined"
          size="small"
          placeholder="/collections or /collections/AYURVEDA"
          value={sliderContext.state.link || ""}
          onChange={(e) => {
            sliderContext.setState({
              ...sliderContext.state,
              link: e.target.value,
            });
          }}
          helperText="Where users navigate when clicking this banner"
        />
      </Grid2>

      {/* Active Toggle */}
      <Grid2 xs={12}>
        <FormControlLabel
          control={
            <Switch
              disabled={saving}
              checked={sliderContext.state.isActive ?? true}
              onChange={(e) => {
                sliderContext.setState({
                  ...sliderContext.state,
                  isActive: e.target.checked,
                });
              }}
              color="primary"
            />
          }
          label="Active (Visible on Homepage Carousel)"
        />
      </Grid2>

      {/* Action Buttons */}
      <Grid2 xs={12} mt={1}>
        <CustomButton
          variant="contained"
          color="primary"
          onClick={() => {
            handleSetSlider(action);
          }}
          fullWidth
          disabled={saving || isUploading || !sliderContext.state.imageUrl}
          sx={{ bgcolor: "#004d40", borderRadius: "10px", py: 1.2, fontWeight: 800, textTransform: "none", "&:hover": { bgcolor: "#00382e" } }}
        >
          {saving ? "Saving…" : action === "ADD" ? "Add Banner Image" : "Save Changes"}
        </CustomButton>

        <CustomButton
          variant="outlined"
          color="inherit"
          onClick={hideModal}
          disabled={saving || isUploading}
          fullWidth
          sx={{ marginTop: "10px", borderRadius: "10px", borderColor: "#cbd5e1", color: "#475569", textTransform: "none", fontWeight: 700 }}
        >
          Cancel
        </CustomButton>
      </Grid2>
    </Grid2>
  );
};

export default AddSlider;
